import {
	ArrowUp,
	Copy,
	Loader2,
	PanelLeftOpen,
	RotateCcw,
	ThumbsDown,
	ThumbsUp,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, type FormEvent, type RefObject } from "react";
import { Streamdown } from "streamdown";
import { AgentQuestionOptions } from "@/components/ui/agent-question-options";
import { DeletionConfirm } from "@/components/ui/deletion-confirm";
import { Button } from "@/components/ui/button";
import { answerableCreativeCards, CreativeCardFor } from "@/components/workspace/creative-card";
import { isDeletionQuestion, type AgentQuestion } from "@/hooks/use-agent-questions";
import { useTranslations } from "@/hooks/use-translations";
import type { ChatHistoryMessage } from "@/lib/api";
import {
	ChipIcon,
	CollapsePanelIcon,
	GlobeIcon,
	PaperclipIcon,
} from "./canvas-icons";

interface CanvasChatPanelProps {
	isOpen: boolean;
	onClose: () => void;
	messages: ChatHistoryMessage[];
	isLoading: boolean;
	isSending: boolean;
	pendingQuestion: AgentQuestion | null;
	onAgentOptionSelect: (label: string) => void;
	/** Delete or Cancel on a deletion the agent waits for; the input is blocked meanwhile (M-05). */
	onDeletionChoice: (choice: "delete" | "cancel") => void;
	isAnsweringDeletion?: boolean;
	chatInput: string;
	onChatInputChange: (value: string) => void;
	onSubmit: (e: FormEvent) => void;
	chatEndRef: RefObject<HTMLDivElement | null>;
}

/** Left chat sidebar of the Canvas page. */
export function CanvasChatPanel({
	isOpen,
	onClose,
	messages,
	isLoading,
	isSending,
	pendingQuestion,
	onAgentOptionSelect,
	onDeletionChoice,
	isAnsweringDeletion,
	chatInput,
	onChatInputChange,
	onSubmit,
	chatEndRef,
}: CanvasChatPanelProps) {
	const t = useTranslations("workspace");
	const answerable = useMemo(() => answerableCreativeCards(messages), [messages]);

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.aside
					initial={{ width: 0, opacity: 0 }}
					animate={{ width: 409, opacity: 1 }}
					exit={{ width: 0, opacity: 0 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					className="flex-shrink-0 mt-4 mb-4 rounded-2xl ml-4 border border-border-subtle bg-bg-surface flex flex-col overflow-hidden"
				>
					{/* Chat Header */}
					<div className="flex items-center justify-between px-4 pt-4 pb-2">
						<h2 className="text-base font-semibold text-text-primary">
							{t("chat")}
						</h2>
						<Button variant="ghost" size="icon-sm" onClick={onClose}>
							<CollapsePanelIcon />
						</Button>
					</div>

					<div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-thin">
						{isLoading && messages.length === 0 ? (
							<div className="flex items-center justify-center py-12">
								<Loader2 size={24} className="animate-spin text-text-tertiary" />
							</div>
						) : messages.length === 0 && !isSending ? (
							<div className="flex items-center justify-center py-12">
								<p className="text-sm text-text-tertiary">
									No messages yet. Start a conversation!
								</p>
							</div>
						) : (
							<>
								{messages.map((msg) => (
									<div key={msg.id} className="mb-4">
										{msg.role === "assistant" ? (
											<div>
												<div className="flex items-center gap-2 mb-2">
													<span className="text-sm font-semibold text-text-primary">
														Minora
													</span>
												</div>
												<div className="text-sm text-text-primary leading-relaxed chat-markdown chat-message-markdown">
													<Streamdown>{msg.content || ""}</Streamdown>
												</div>
												<CreativeCardFor message={msg} answerable={answerable} />
												<div className="flex items-center gap-1 mt-2 pl-9">
													<Button variant="ghost" size="icon-xs">
														<Copy size={13} />
													</Button>
													<Button variant="ghost" size="icon-xs">
														<RotateCcw size={13} />
													</Button>
													<Button variant="ghost" size="icon-xs">
														<ThumbsUp size={13} />
													</Button>
													<Button variant="ghost" size="icon-xs">
														<ThumbsDown size={13} />
													</Button>
												</div>
											</div>
										) : (
											<div className="flex justify-end">
												<div className="max-w-[85%] px-4 py-3 rounded-2xl bg-brand-100 text-text-primary text-sm">
													<Streamdown>{msg.content || ""}</Streamdown>
												</div>
											</div>
										)}
									</div>
								))}
								{isSending && (
									<div className="mb-4 animate-pulse">
										<div className="flex items-center gap-2 mb-2">
											<span className="text-sm font-semibold text-text-primary">
												Minora
											</span>
										</div>
										<div className="text-sm text-text-tertiary italic">
											Thinking...
										</div>
									</div>
								)}
							</>
						)}
						<div ref={chatEndRef} />
					</div>

					{/* A deletion: only Delete or Cancel answers it */}
					{pendingQuestion && isDeletionQuestion(pendingQuestion) ? (
						<div className="px-4">
							<DeletionConfirm
								question={pendingQuestion}
								onChoose={onDeletionChoice}
								disabled={isAnsweringDeletion}
							/>
						</div>
					) : pendingQuestion?.options?.length ? (
						<div className="px-4">
							<AgentQuestionOptions
								options={pendingQuestion.options}
								onSelect={onAgentOptionSelect}
								disabled={isSending}
							/>
						</div>
					) : null}

					{/* Chat Input */}
					<div className="px-4 pb-4 pt-2">
						<form onSubmit={onSubmit}>
							<div className="relative rounded-2xl bg-bg-surface border border-border-control shadow-lg focus-within:ring-2 focus-within:ring-focus-ring focus-within:ring-offset-2 focus-within:ring-offset-bg-surface">
								<input
									type="text"
									value={chatInput}
									onChange={(e) => onChatInputChange(e.target.value)}
									placeholder={isDeletionQuestion(pendingQuestion) ? t("deletion.blocked") : t("askAnything")}
									disabled={isDeletionQuestion(pendingQuestion)}
									className="w-full bg-transparent text-text-primary placeholder:text-text-tertiary text-sm px-4 py-3.5 pr-28 focus:outline-none disabled:cursor-not-allowed"
								/>
								<div className="absolute right-2 top-1/2 bg-bg-surface rounded-full border border-border-subtle p-1 -translate-y-1/2 flex items-center gap-0.5">
									<Button
										type="button"
										variant="ghost"
										size="icon-xs"
										className="rounded-full"
									>
										<ChipIcon />
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon-xs"
										className="rounded-full"
									>
										<GlobeIcon />
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon-xs"
										className="rounded-full"
									>
										<PaperclipIcon />
									</Button>
									<Button
										type="submit"
										size="icon-sm"
										disabled={isDeletionQuestion(pendingQuestion) || !chatInput.trim()}
										className="rounded-full"
									>
										<ArrowUp size={15} strokeWidth={2.5} />
									</Button>
								</div>
							</div>
						</form>
					</div>
				</motion.aside>
			)}
		</AnimatePresence>
	);
}

/** Floating button that re-opens the chat sidebar when it is closed. */
export function CanvasChatOpenButton({ onOpen }: { onOpen: () => void }) {
	return (
		<Button
			asChild
			variant="secondary"
			size="icon"
			className="absolute top-4 left-4 z-20"
		>
			<motion.button
				initial={{ opacity: 0, x: -10 }}
				animate={{ opacity: 1, x: 0 }}
				onClick={onOpen}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<PanelLeftOpen size={18} />
			</motion.button>
		</Button>
	);
}
